/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\AiProfileTTSModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import { Download, Volume2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';

interface AzureVoiceData {
  ShortName: string;
  Name: string;
  Locale: string;
  DisplayLanguage?: string;
}

interface SelectedProvider {
  provider: 'azure' | 'openai' | 'google' | string;
}

interface ModalTestConfig {
  pitchValue: number;
  volumeValue: number;
  rateValue: number;
  personality: string;
  voice: string;
}

interface AiProfileTTSModalProps {
  isOpen: boolean;
  selectedProvider: SelectedProvider | null;
  selectedVoiceData: AzureVoiceData | null;
  formData: {
    model?: string;
    voice_config?: string | Record<string, unknown>;
  };
  testText: string;
  setTestText: (text: string | ((prev: string) => string)) => void;
  testingTTS: boolean;
  ttsError: string | null;
  setTtsError: (err: string | null) => void;
  audioUrl: string | null;
  downloadingProductionAudio: boolean;
  modalTestConfig: ModalTestConfig;
  hasModalChanges: () => boolean;
  onClose: () => void;
  onGenerateTTS: () => void;
  onApplyChanges: () => void;
  onDownloadProduction: () => void;
  t: (key: string, fallback?: string) => string;
}

export function AiProfileTTSModal({
  isOpen, selectedProvider, selectedVoiceData, formData,
  testText, setTestText, testingTTS, ttsError, setTtsError,
  audioUrl, downloadingProductionAudio, modalTestConfig,
  hasModalChanges, onClose, onGenerateTTS, onApplyChanges, onDownloadProduction, t,
}: AiProfileTTSModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-lg shadow-lg max-w-2xl w-full mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {t('aiProfiles.voiceConfig.testVoice')}
            </h3>
            <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Text input */}
          <div className="mb-4">
            <Label htmlFor="modal_test_text" className="text-sm font-medium text-foreground mb-2 block">
              {t('aiProfiles.voiceConfig.textToConvert')}
            </Label>
            <textarea
              id="modal_test_text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder={t('aiProfiles.voiceConfig.enterTextPlaceholder')}
              className="w-full min-h-[100px] px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y text-foreground"
              rows={4}
            />
          </div>

          {/* Voice configuration summary */}
          <div className="mb-4">
            <Label className="text-sm font-medium text-foreground mb-2 block">
              {t('aiProfiles.voiceConfig.voiceConfiguration')}:
            </Label>
            <div className="text-sm text-muted-foreground">
              {selectedProvider?.provider === 'azure' && selectedVoiceData && (
                <div>
                  <p><strong>{t('aiProfiles.voiceConfig.voice')}:</strong> {selectedVoiceData.ShortName}</p>
                  <p><strong>{t('aiProfiles.voiceConfig.language')}:</strong> {selectedVoiceData.DisplayLanguage || selectedVoiceData.Locale}</p>
                </div>
              )}
              {selectedProvider?.provider === 'openai' && (
                <div>
                  <p><strong>{t('aiProfiles.voiceConfig.voice')}:</strong> {modalTestConfig.voice || 'alloy'}</p>
                  <p><strong>Model:</strong> {formData.model || 'tts-1'}</p>
                </div>
              )}
              {selectedProvider?.provider === 'google' && formData.voice_config && (
                <div>
                  {(() => {
                    try {
                      const voiceConfig = typeof formData.voice_config === 'string'
                        ? JSON.parse(formData.voice_config)
                        : formData.voice_config;
                      return (
                        <>
                          <p><strong>{t('aiProfiles.voiceConfig.voice')}:</strong> {(voiceConfig as Record<string, string>).voice || 'No seleccionada'}</p>
                          <p><strong>{t('aiProfiles.voiceConfig.language')}:</strong> {(voiceConfig as Record<string, string>).language} ({(voiceConfig as Record<string, string>).accent})</p>
                          <p><strong>{t('aiProfiles.googleTts.speed') || 'Velocidad'}:</strong> {String((voiceConfig as Record<string, unknown>).speakingRate ?? 1.0)}x</p>
                        </>
                      );
                    } catch {
                      return <p className="text-muted-foreground">No hay configuraci�n de voz</p>;
                    }
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* SSML Effects - Only for Google TTS */}
          {selectedProvider?.provider === 'google' && (
            <div className="mb-4">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">
                {t('aiProfiles.googleTts.effectsAndActions') || 'Efectos y Acciones'}
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { text: ' [pausa 2s] ', emoji: '??', label: t('aiProfiles.googleTts.pause') || 'Pausa (2s)' },
                  { text: ' [risa] ', emoji: '??', label: t('aiProfiles.googleTts.laugh') || 'Risa' },
                  { text: ' [grito] ', emoji: '??', label: t('aiProfiles.googleTts.shout') || 'Grito' },
                  { text: ' [llanto] ', emoji: '??', label: t('aiProfiles.googleTts.cry') || 'Llanto' },
                ].map(({ text, emoji, label }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setTestText((prev: string) => prev + text)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-primary/10 hover:border-primary/50 transition-colors text-sm font-medium text-foreground"
                  >
                    <span className="text-lg">{emoji}</span> {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading spinner */}
          {testingTTS && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-muted-foreground">{t('aiProfiles.voiceConfig.generatingAudio')}</p>
            </div>
          )}

          {/* Error */}
          {ttsError && !testingTTS && (
            <div className="py-4">
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{ttsError}</p>
              </div>
            </div>
          )}

          {/* Audio player */}
          {audioUrl && !testingTTS && !ttsError && (
            <div className="py-4">
              <Label className="text-sm font-medium text-foreground mb-2 block">
                {t('aiProfiles.voiceConfig.generatedAudio')}
              </Label>
              <audio
                controls
                autoPlay
                className="w-full"
                key={audioUrl}
                onError={() => setTtsError(t('aiProfiles.voiceConfig.failedToLoadAudio'))}
              >
                <source src={audioUrl} type="audio/mpeg" />
                {t('aiProfiles.voiceConfig.audioNotSupported')}
              </audio>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 justify-end items-center mt-6 flex-wrap">
            {audioUrl && !testingTTS && !ttsError && (
              <Button
                type="button"
                onClick={onDownloadProduction}
                disabled={downloadingProductionAudio || !testText.trim()}
                variant="outline"
                className="flex-shrink-0"
              >
                {downloadingProductionAudio ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    {t('aiProfiles.voiceConfig.downloading')}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    <span className="whitespace-nowrap">{t('aiProfiles.voiceConfig.downloadProduction')}</span>
                  </>
                )}
              </Button>
            )}
            <Button type="button" onClick={onClose} variant="outline" className="flex-shrink-0">
              {t('aiProfiles.voiceConfig.close')}
            </Button>
            <Button
              type="button"
              onClick={onApplyChanges}
              variant="outline"
              className="flex-shrink-0"
              disabled={!hasModalChanges()}
            >
              {t('aiProfiles.voiceConfig.applyChanges')}
            </Button>
            {!testingTTS && (
              <Button type="button" onClick={onGenerateTTS} disabled={!testText.trim()} className="flex-shrink-0">
                <Volume2 className="h-4 w-4 mr-2" />
                {audioUrl ? t('aiProfiles.voiceConfig.regenerate') : t('aiProfiles.voiceConfig.generate')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
